import { validateCron } from "@prodgy/schedule";

import type { FormFieldsMetadata } from "@/shared/types/magicFillTypes";

/** Dias úteis às 9h: o ponto de partida quando a proposta não traz um horário que sirva. */
export const FALLBACK_CRON = "0 9 * * 1-5";

export interface ProposalSkill {
  name: string;
  title: string | null;
  description: string;
}

export interface JobProposal {
  name: string;
  description: string;
  instruction: string;
  triggerType: "schedule" | "webhook";
  cron: string;
  /** Por que essa frequência, na voz da IA — mostrado acima do gatilho. */
  reason: string;
}

/**
 * O pedido ao Magic Fill (AB#86605, task 86874): a Skill vai preenchida, como
 * contexto, e os campos do job vão vazios — são esses que a IA propõe.
 */
export function proposalRequest(skill: ProposalSkill): {
  fields: Record<string, string>;
  fieldMetadata: FormFieldsMetadata;
} {
  return {
    fields: {
      skill_name: skill.name,
      skill_title: skill.title ?? "",
      skill_description: skill.description,
      name: "",
      description: "",
      instruction: "",
      trigger_type: "",
      cron: "",
      schedule_reason: "",
    },
    fieldMetadata: {
      name: { type: "text", maxLength: 60 },
      description: { type: "text", maxLength: 160 },
      instruction: { type: "textarea", maxLength: 2000 },
      trigger_type: { type: "select", options: ["schedule", "webhook"] },
      cron: { type: "text", maxLength: 40 },
      schedule_reason: { type: "text", maxLength: 200 },
    },
  };
}

function text(value: unknown): string {
  return typeof value === "string" ? value.trim() : "";
}

/**
 * A resposta do Magic Fill virada em valores do formulário. A proposta é
 * conferida, não confiada: a instrução sempre cita a Skill (se a IA esqueceu,
 * a citação entra no começo), e um cron que não tem cinco campos dá lugar ao
 * padrão — melhor um horário comum do que um campo inválido no formulário.
 */
export function applyProposal(suggested: Record<string, unknown> | undefined, skillName: string): JobProposal {
  const mention = `@skill:${skillName}`;
  const instruction = text(suggested?.instruction);
  const triggerType = text(suggested?.trigger_type) === "webhook" ? "webhook" : "schedule";
  const cron = text(suggested?.cron);
  return {
    name: text(suggested?.name).slice(0, 60),
    description: text(suggested?.description),
    instruction: instruction.includes(mention) ? instruction : `${mention} ${instruction}`.trim(),
    triggerType,
    cron: triggerType === "schedule" && validateCron(cron).ok ? cron : FALLBACK_CRON,
    reason: text(suggested?.schedule_reason),
  };
}
